import {
  moderationSystemPrompt,
  normalizeModerationResults,
  type AIModel,
  type AIProvider,
  type ModerationInput,
  type ModerationResult,
} from '@wilson/shared'
import { z } from 'zod'

export interface BrowserAIOptions {
  provider: 'ollama' | 'openai-compatible'
  baseUrl: string
  model: string
  apiKey?: string
}

function parse(text: string, inputs: ModerationInput[]) {
  let results: ModerationResult[]
  try {
    const json = JSON.parse(
      text
        .trim()
        .replace(/^```json\s*/i, '')
        .replace(/\s*```$/, ''),
    ) as unknown
    const candidate =
      typeof json === 'object' && json !== null && 'results' in json ? json.results : json
    results = normalizeModerationResults(candidate)
  } catch (cause) {
    throw new Error('The AI returned malformed results. No moderation results were stored.', {
      cause,
    })
  }
  const ids = new Set(inputs.map(({ id }) => id))
  const resultIds = new Set(results.map(({ commentId }) => commentId))
  if (
    ids.size !== inputs.length ||
    results.length !== inputs.length ||
    resultIds.size !== ids.size ||
    results.some(({ commentId }) => !ids.has(commentId))
  ) {
    throw new Error('The model returned results for unexpected comments')
  }
  return results
}

export function createBrowserAI(options: BrowserAIOptions): AIProvider {
  const headers = {
    'content-type': 'application/json',
    ...(options.apiKey ? { authorization: `Bearer ${options.apiKey}` } : {}),
  }
  return {
    async listModels(): Promise<AIModel[]> {
      if (options.provider === 'ollama') {
        const response = await fetch(new URL('/api/tags', options.baseUrl), {
          signal: AbortSignal.timeout(8_000),
        })
        if (!response.ok) throw new Error('Could not discover Ollama models')
        const data = z
          .object({ models: z.array(z.object({ name: z.string() })) })
          .parse(await response.json())
        return data.models.map(({ name }) => ({ id: name, name }))
      }
      const response = await fetch(new URL('/v1/models', options.baseUrl), {
        headers,
        signal: AbortSignal.timeout(8_000),
      })
      if (!response.ok) throw new Error('Could not discover models')
      const data = z
        .object({ data: z.array(z.object({ id: z.string() })) })
        .parse(await response.json())
      return data.data.map(({ id }) => ({ id, name: id }))
    },
    async testConnection() {
      try {
        await this.listModels()
        return true
      } catch {
        return false
      }
    },
    async moderate(messages: ModerationInput[]): Promise<ModerationResult[]> {
      const url = new URL(
        options.provider === 'ollama' ? '/api/chat' : '/v1/chat/completions',
        options.baseUrl,
      )
      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(
          options.provider === 'ollama'
            ? {
                model: options.model,
                stream: false,
                format: 'json',
                messages: [
                  { role: 'system', content: moderationSystemPrompt },
                  { role: 'user', content: JSON.stringify(messages) },
                ],
              }
            : {
                model: options.model,
                temperature: 0,
                response_format: { type: 'json_object' },
                messages: [
                  { role: 'system', content: moderationSystemPrompt },
                  { role: 'user', content: JSON.stringify(messages) },
                ],
              },
        ),
        signal: AbortSignal.timeout(120_000),
      })
      if (!response.ok) throw new Error('The browser AI provider failed')
      const json: unknown = await response.json()
      const content =
        options.provider === 'ollama'
          ? z.object({ message: z.object({ content: z.string() }) }).parse(json).message.content
          : z
              .object({
                choices: z.array(z.object({ message: z.object({ content: z.string() }) })).min(1),
              })
              .parse(json).choices[0]!.message.content
      return parse(content, messages)
    },
  }
}
